import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { compact } from 'lodash-es';

import { AclosetMessageError } from '@looko-corp/acloset-react-kit';

import type { AclosetPlanActionCode, AclosetServicePricingCodeCode } from '@/MetadataType';
import type { DomainAvailableFeatureResponse } from '@/Swagger';
import { aimsApi } from '@/axios/AclosetApi';

export interface DomainPlanExt {
  /**
   * 사용 가능한 Feature 셋.
   */
  readonly availableFeaturesSet: Set<string>;

  availableFeatures: Array<DomainAvailableFeatureResponse>;

  /**
   * feature 사용 가능 여부.
   */
  enabled(code: AclosetPlanActionCode): boolean;

  availableServiceSet: Set<string>;
  /**
   * service 사용 가능 여부.
   */
  enabledService(code: AclosetServicePricingCodeCode): boolean;

  lastServiceChangeDate?: string;
}

declare module '@/Swagger' {
  interface DomainGetMyServicePricingsResponse {
    extendedPlan: DomainPlanExt;
  }
}

export const planKeys = {
  list: ['plans'] as const,
  all: ['plan'] as const,
  detail: (shopID: string) => [...planKeys.all, shopID] as const,
  status: (shopID: string) => [...planKeys.detail(shopID), 'status'] as const,
} as const;

/**
 * 해당 플랜은 샵과 상관없는 플랜의 프리셋 속성을 가지고 있습니다.
 */
export const usePlansList = () => {
  return useQuery({
    queryKey: planKeys.list,

    queryFn: async () => {
      const v = await aimsApi.shops.plansList();
      return v.data;
    },
  });
};

/**
 * usePlansList suspense.
 */
export const useSuspensePlansList = () => {
  return useSuspenseQuery({
    queryKey: planKeys.list,
    queryFn: async () => {
      const v = await aimsApi.shops.plansList();
      return v.data;
    },
  });
};

/**
 * 샵에 적용이 되어있는 플랜을 단건 조회합니다.
 */
export const useShopPlansDetail = (shopID: string) => {
  return useQuery({
    queryKey: planKeys.detail(shopID),
    queryFn: async () => {
      if (!shopID) {
        throw new AclosetMessageError('Empty shop ID');
      }

      return aimsApi.shops.servicePricingsList2(shopID).then(v => {
        v.data.extendedPlan = {
          availableFeaturesSet: new Set(
            compact(v.data.servicePricings?.flatMap(v => v.availableFeatures.map(v => v.code))),
          ),

          availableServiceSet: new Set(compact(v.data.servicePricings?.flatMap(v => v.code))),
          availableFeatures: v.data.servicePricings?.flatMap(v => v.availableFeatures) ?? [],

          enabled(code) {
            return this.availableFeaturesSet.has(code);
          },

          enabledService(code) {
            return this.availableServiceSet.has(code);
          },

          lastServiceChangeDate: v.data.lastServiceChangeDate,
        };

        return v.data;
      });
    },
    enabled: !!shopID,
  });
};
